import { NavLink } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { perfilPodeAcessarPath } from '../lib/perfilAcesso'
import { PERFIL_LABELS, type PerfilUsuario } from '../types/database'
import { useConnection } from '../hooks/useConnection'
import './layout.css'

interface NavItem {
  to: string
  label: string
  end?: boolean
  adminOnly?: boolean
  auditoria?: boolean
  fiscalOnly?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Principal',
    items: [
      { to: '/', label: 'Dashboard', end: true },
      { to: '/acompanhamento', label: 'Acompanhamento' },
    ],
  },
  {
    label: 'Planejamento',
    items: [
      { to: '/calendario', label: 'Calendário' },
      { to: '/ficha-tecnica', label: 'Ficha técnica' },
      { to: '/alimentos', label: 'Alimentos' },
    ],
  },
  {
    label: 'Operação',
    items: [
      { to: '/planejamento-rota', label: 'Planejamento de rota' },
      { to: '/entregas', label: 'Entregas' },
      { to: '/estoque', label: 'Estoque' },
    ],
  },
  {
    label: 'Financeiro',
    items: [
      { to: '/pedido-mensal', label: 'Pedido', fiscalOnly: true },
      { to: '/contrato', label: 'Contrato' },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/configuracoes', label: 'Configurações', adminOnly: true },
      { to: '/auditoria', label: 'Auditoria', auditoria: true },
    ],
  },
]

function isVisible(item: NavItem, perfil: PerfilUsuario | undefined) {
  if (!perfil) return false

  // Whitelist: SEMED, logística, escola e Nutricionista ISEN (fiscal)
  if (
    perfil === 'responsavel_escola' ||
    perfil === 'logistica' ||
    perfil === 'nutricionista' ||
    perfil === 'fiscal'
  ) {
    return perfilPodeAcessarPath(perfil, item.to)
  }

  if (item.adminOnly) return perfil === 'admin'
  if (item.auditoria) return perfil === 'admin' || perfil === 'fiscal'
  if (item.fiscalOnly) return perfil === 'admin' || perfil === 'fiscal'
  return true
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { profile, signOut, isLocalDev } = useAuth()
  const { online } = useConnection()
  const today = new Date().toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

  const visibleGroups = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => isVisible(item, profile?.perfil)),
  })).filter((group) => group.items.length > 0)

  return (
    <div className="app-shell">
      {!online && (
        <div className="connection-banner" role="alert">
          Sem conexão com a internet. As operações estão bloqueadas até a rede voltar.
        </div>
      )}
      {isLocalDev && (
        <div className="connection-banner" style={{ background: 'var(--color-info)' }} role="status">
          Modo local sem Supabase — dados da API não carregam até configurar o .env.
        </div>
      )}
      <div className="app-body">
        <nav className="app-nav" aria-label="Principal">
          <div className="app-nav-brand">
            <strong>ISEN EDUCAE</strong>
            <span>Timon / MA · SEMED</span>
          </div>
          <div className="app-nav-menu">
            {visibleGroups.map((group) => (
              <div className="app-nav-group" key={group.label}>
                <div className="app-nav-group-label">{group.label}</div>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end ?? false}
                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
          <div className="app-nav-footer">
            Perfil: {profile ? PERFIL_LABELS[profile.perfil] : ''}
            <br />
            {profile?.nome}
          </div>
        </nav>
        <div className="app-content">
          <header className="app-header">
            <div className="app-header-breadcrumb">{today}</div>
            <div className="header-meta">
              <span className={`conn-dot ${online ? 'online' : 'offline'}`} title={online ? 'Online' : 'Offline'} />
              <span className="header-user">{profile?.nome}</span>
              <button type="button" className="btn btn-ghost" onClick={() => signOut()}>
                Sair
              </button>
            </div>
          </header>
          <main className="app-main">{children}</main>
        </div>
      </div>
    </div>
  )
}
